import React, { ReactNode } from 'react';
import ButtonThatOpensPopup from '@app/components/popup/ButtonThatOpensPopup';
import { FormInputProps } from '@app/utils/form/form';
import { ForwardingFC } from '@app/domain/technicals/components';
import { observer } from 'mobx-react';
import InstructionsSelector from '@mortee/routes/validationSystem/instructionsEdit/InstructionsSelector';
import { SupplierRegistrationProcessInstructionType } from '@app/domain/commonSupplierRegistration';
import {
  getInstructionType,
  getSvmInstructionType,
  renderInstructionType,
  renderSvmInstructionTypeWithDoubleBorder,
} from '@mortee/domain/validationSystemInfoComponents';
import styled from '@emotion/styled';
import { isLeavingUnassignableInstructionType, SupplierValidationRecordInstructionType } from '@mortee/domain/validationSystem';
import { showYesNoModalAsync } from '@app/components/Modal';

type Props = FormInputProps<SupplierValidationRecordInstructionType, false> & {
  id: string;
  dataTestId?: string;
  instructionFromRegistrationForm?: SupplierRegistrationProcessInstructionType | null | undefined;
};

const SelectInstructionsInput: ForwardingFC<HTMLButtonElement, Props> = observer(
  React.forwardRef(
    ({ id, dataTestId = id, value: currentInstruction, onChange, accessibilityLabel, instructionFromRegistrationForm }, ref) => {
      let buttonContent;
      let ariaLabel;
      if (currentInstruction) {
        buttonContent = renderSvmInstructionTypeWithDoubleBorder(currentInstruction);
        ariaLabel = `${currentInstruction} - ${accessibilityLabel}`;
      } else if (!currentInstruction && instructionFromRegistrationForm) {
        buttonContent = (
          <ButtonContent>
            {renderInstructionType(instructionFromRegistrationForm)}
            <div>From registration</div>
          </ButtonContent>
        );
        ariaLabel = getInstructionType(instructionFromRegistrationForm)?.name;
      } else {
        buttonContent = <div>From registration</div>;
        ariaLabel = accessibilityLabel;
      }

      async function handleInstructionTypeChange(
        instructionType: SupplierValidationRecordInstructionType | undefined,
      ): Promise<void> {
        const newInstructionType = instructionType ?? instructionFromRegistrationForm;

        // Only a manual type is lost: removing the manual instruction returns to the registration's type, also after re-linking it
        if (isLeavingUnassignableInstructionType(currentInstruction, newInstructionType)) {
          const currentInstructionTypeName = getSvmInstructionType(currentInstruction)?.name ?? currentInstruction;
          const isConfirmed = await showYesNoModalAsync(
            `This record uses an inactive instruction type: ${currentInstructionTypeName}. Inactive types can no longer be assigned, so once you save the change, this record can't be set back to it.`,
            'Change an inactive instruction type?',
            'Change',
            'Cancel',
          );
          if (!isConfirmed) {
            return;
          }
        }

        onChange?.(instructionType);
      }

      return (
        <ButtonThatOpensPopup
          id={id}
          dataTestId={dataTestId}
          appearance='text'
          cornerType='circle'
          colorScheme='grey'
          shadowType='none'
          popupPlacement='bottom-start'
          popupContent={(onDone): ReactNode => (
            <InstructionsSelector
              onClose={onDone}
              onChange={(instructionType): Promise<void> => handleInstructionTypeChange(instructionType)}
            />
          )}
          ref={ref}
          aria-label={ariaLabel}
        >
          {buttonContent}
        </ButtonThatOpensPopup>
      );
    },
  ),
);

export default SelectInstructionsInput;

const ButtonContent = styled.div`
  display: flex;
  flex-direction: column;
`;
